import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * download — pull a report from the API and hand it to the user.
 *
 * Built 26 Aug 2026 alongside the attendance/sponsorship exports.
 *
 * WEB: an object URL + a click on an <a download>, exactly as before.
 *
 * NATIVE (Week 4 store build, Sept 2026): a blob URL has nowhere to go inside
 * the Capacitor shell, so the file is written to the app's cache directory
 * (@capacitor/filesystem) and handed to the OS share sheet (@capacitor/share)
 * — "Save to Files" / "Save to Drive" / WhatsApp it to the treasurer. Auth is
 * the bearer token we already hold, so no new backend surface was needed.
 * Every caller gets the native path for free (the old `canDownload()` gate is
 * gone — downloads work everywhere now).
 */

const isNative = () => Capacitor.isNativePlatform();

function filenameFrom(headers, fallback) {
  // Requires Content-Disposition in the CORS exposedHeaders (backend/index.js)
  // — cross-origin it is invisible otherwise, which is the native case.
  const cd = headers?.['content-disposition'] || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  return match ? decodeURIComponent(match[1]) : fallback;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Hand a Blob to the user on the current platform.
 * Returns { shared: true } on native (the share sheet was shown; the user may
 * have cancelled — that is not an error), { shared: false } on web.
 */
async function deliverBlob(blob, name) {
  if (!isNative()) {
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick — revoking synchronously can cancel the download
    // in Safari before it has read the blob.
    setTimeout(() => URL.revokeObjectURL(href), 1000);
    return { shared: false };
  }

  const data = await blobToBase64(blob);
  // Cache dir: app-private, cleared by the OS when space is tight, and covered
  // by the FileProvider <cache-path> Capacitor ships in file_paths.xml, which
  // is what lets the share sheet read it on Android.
  const saved = await Filesystem.writeFile({ path: name, data, directory: Directory.Cache });
  try {
    await Share.share({ title: name, url: saved.uri, dialogTitle: 'Save or send this file' });
  } catch (err) {
    // Dismissing the sheet rejects on Android ("Share canceled") — not a failure,
    // but not a save either; the caller words it neutrally (marakin-qa S5).
    if (!/cancel/i.test(String(err?.message || err))) throw err;
    return { shared: true, cancelled: true };
  }
  return { shared: true, cancelled: false };
}

/**
 * @param {string} url    API path, e.g. `/clubs/nb-test/reports/attendance/export`
 * @param {object} params query params (period, format, from, to, teamId…)
 * @param {string} fallbackName used if the server didn't name the file
 */
export async function downloadReport(url, params = {}, fallbackName = 'marakin-report') {
  // validateStatus lets US handle the error. Without it axios rejects first
  // and the caller shows "Request failed with status code 403" instead of the
  // sentence the API actually wrote — and the error body, being a blob, would
  // otherwise never be read at all.
  const res = await axios.get(url, {
    params,
    responseType: 'blob',
    validateStatus: () => true,
  });

  if (res.status >= 400) {
    let message = 'That report could not be generated.';
    try {
      const text = await res.data.text();
      message = JSON.parse(text).error || message;
    } catch { /* non-JSON body — keep the default */ }
    throw new Error(message);
  }

  const name = filenameFrom(res.headers, fallbackName);
  const { shared, cancelled } = await deliverBlob(res.data, name);

  // The API says so explicitly when it hit its row ceiling, so the UI can warn
  // instead of the club trusting a quietly partial file.
  return { name, shared, cancelled, truncated: res.headers?.['x-report-truncated'] === 'true' };
}

/**
 * downloadLocalFile — hand the user a file generated in the browser (no API
 * round-trip). Used for the bulk-add templates. Same web/native split as
 * downloadReport — returns its promise, so callers should catch (the native
 * path can fail). BOM prefix so Excel opens UTF-8 CSVs with accented names intact.
 */
export function downloadLocalFile(name, content, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob(['﻿' + content], { type: mime });
  return deliverBlob(blob, name);
}
